import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import './PictureViewer.css'

/**
 * One picture, larger, over the page
 * -------------------------------------------------------------
 * Opened by clicking a picture in a conversation (Huanrui, 2026-10-01): the
 * feel of Amazon's View details, a little smaller, and the × puts everything
 * back exactly as it was -- nothing underneath moves, scrolls or reloads,
 * because nothing underneath is touched.
 *
 * Esc and a click outside the picture close it too.
 *
 * SHARP WITHOUT WAITING, as on the canvas (nodes.tsx). `src` is what the
 * conversation was already showing, so it is on screen at once; `full`, when
 * there is one, is the full-size file, faded in over it once it has arrived.
 * If it never arrives, the picture already showing simply stays.
 *
 * ZOOM (2026-10-15): press and drag to zoom about the point pressed --
 * right or up goes in, left or down comes out (Photoshop's scrubby zoom). The
 * wheel zooms about the pointer too. Up to 8x. To move a zoomed picture, drag
 * with the middle button or with Space held.
 */

const MAX_ZOOM = 8

interface View {
  s: number
  x: number
  y: number
}

/** Keep the picture covering its box: no panning off into the white. */
function held(view: View, width: number, height: number): View {
  if (view.s <= 1) return { s: 1, x: 0, y: 0 }
  return {
    s: view.s,
    x: Math.min(0, Math.max(width - width * view.s, view.x)),
    y: Math.min(0, Math.max(height - height * view.s, view.y)),
  }
}
export function PictureViewer({
  src,
  full,
  onClose,
}: {
  src: string
  full?: string
  onClose: () => void
}) {
  const [fullShown, setFullShown] = useState(false)
  const close = useRef<HTMLButtonElement | null>(null)
  const stage = useRef<HTMLDivElement | null>(null)
  const [view, setView] = useState<View>({ s: 1, x: 0, y: 0 })
  const viewNow = useRef(view)
  viewNow.current = view
  const dragging = useRef<{
    mode: 'zoom' | 'pan'
    x: number
    y: number
    /** Where it was pressed, in the stage: the point the zoom is about. */
    px: number
    py: number
    from: View
  } | null>(null)
  const [grabbing, setGrabbing] = useState<'' | 'zoom' | 'pan'>('')
  const space = useRef(false)

  /** Zoom to `next` about a point in the stage. */
  const zoomAt = useCallback((next: number, px: number, py: number) => {
    const box = stage.current
    if (!box) return
    const was = viewNow.current
    const s = Math.min(MAX_ZOOM, Math.max(1, next))
    const k = s / was.s
    setView(held({ s, x: px - (px - was.x) * k, y: py - (py - was.y) * k }, box.clientWidth, box.clientHeight))
  }, [])

  // Not React's onWheel: that one is passive, and the page would scroll too.
  useEffect(() => {
    const box = stage.current
    if (!box) return
    const onWheel = (event: WheelEvent) => {
      event.preventDefault()
      const rect = box.getBoundingClientRect()
      const factor = Math.exp(-event.deltaY * (event.deltaMode === 1 ? 0.05 : 0.0018))
      zoomAt(viewNow.current.s * factor, event.clientX - rect.left, event.clientY - rect.top)
    }
    box.addEventListener('wheel', onWheel, { passive: false })
    return () => box.removeEventListener('wheel', onWheel)
  }, [zoomAt])

  useEffect(() => {
    // Back to whatever had focus before, so a keyboard user lands where they
    // were rather than at the top of the page.
    const before = document.activeElement as HTMLElement | null
    close.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
      if (event.key === ' ') {
        // Space held: a drag moves the picture instead of zooming it.
        event.preventDefault()
        space.current = true
      }
    }
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === ' ') space.current = false
    }
    window.addEventListener('keydown', onKey, true)
    window.addEventListener('keyup', onKeyUp, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      window.removeEventListener('keyup', onKeyUp, true)
      before?.focus?.()
    }
  }, [onClose])

  return createPortal(
    <div className="ww-viewer" role="dialog" aria-modal="true" aria-label="Picture" onClick={onClose}>
      <div
        ref={stage}
        className={`ww-viewer__stage${view.s > 1 ? ' ww-viewer__stage--zoomed' : ''}${
          grabbing ? ` ww-viewer__stage--${grabbing}` : ''
        }`}
        onClick={(event) => event.stopPropagation()}
        // The middle button would start the browser's own autoscroll.
        onMouseDown={(event) => {
          if (event.button === 1) event.preventDefault()
        }}
        onPointerDown={(event) => {
          if (event.button !== 0 && event.button !== 1) return
          if ((event.target as HTMLElement).closest('button')) return
          event.preventDefault()
          const rect = event.currentTarget.getBoundingClientRect()
          const mode = event.button === 1 || space.current ? 'pan' : 'zoom'
          dragging.current = {
            mode,
            x: event.clientX,
            y: event.clientY,
            px: event.clientX - rect.left,
            py: event.clientY - rect.top,
            from: viewNow.current,
          }
          event.currentTarget.setPointerCapture(event.pointerId)
          setGrabbing(mode)
        }}
        onPointerMove={(event) => {
          const drag = dragging.current
          const box = stage.current
          if (!drag || !box) return
          const dx = event.clientX - drag.x
          const dy = event.clientY - drag.y
          if (drag.mode === 'pan') {
            setView(held({ s: drag.from.s, x: drag.from.x + dx, y: drag.from.y + dy }, box.clientWidth, box.clientHeight))
            return
          }
          // Right or up goes in; about 200px of drag doubles it.
          const s = Math.min(MAX_ZOOM, Math.max(1, drag.from.s * Math.pow(2, (dx - dy) / 200)))
          const k = s / drag.from.s
          setView(
            held(
              { s, x: drag.px - (drag.px - drag.from.x) * k, y: drag.py - (drag.py - drag.from.y) * k },
              box.clientWidth,
              box.clientHeight,
            ),
          )
        }}
        onPointerUp={() => {
          dragging.current = null
          setGrabbing('')
        }}
        onPointerCancel={() => {
          dragging.current = null
          setGrabbing('')
        }}
      >
        <div
          className="ww-viewer__pan"
          style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})` }}
        >
          {/* Gone once the full-size one is in (2026-11-02): when the two are
              not the same shape -- Amazon's search picture is cut differently
              from the product's own -- the small one showed round the edges
              of the large one. */}
          <img
            className="ww-viewer__img ww-viewer__img--small"
            src={src}
            alt=""
            draggable={false}
            style={{ opacity: full && full !== src && fullShown ? 0 : 1 }}
          />
          {full && full !== src ? (
            <img
              className="ww-viewer__img ww-viewer__img--full"
              src={full}
              alt=""
              draggable={false}
              style={{ opacity: fullShown ? 1 : 0 }}
              onLoad={() => setFullShown(true)}
            />
          ) : null}
        </div>
        {view.s > 1 ? (
          <button
            type="button"
            className="ww-viewer__zoom"
            title="Back to fit"
            onClick={() => setView({ s: 1, x: 0, y: 0 })}
          >
            {Math.round(view.s * 100)}%
          </button>
        ) : null}
        <button
          ref={close}
          type="button"
          className="ww-viewer__close"
          aria-label="Close"
          title="Close"
          onClick={onClose}
        >
          {/* Drawn, not typed: a × character sits on the font's baseline and
              looked off centre in the ring (2026-10-15). */}
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M2 2 L12 12 M12 2 L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>,
    document.body,
  )
}
